import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config/apiConfig';
import './AppealFlow.css';

const CURRENT_YEAR = new Date().getFullYear();

function fmt$(n) {
  if (!n && n !== 0) return '-';
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtNum(n) {
  if (!n && n !== 0) return '-';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtRounded(n) {
  if (!n && n !== 0) return '-';
  const rounded = Math.round(Number(n) / 100) * 100;
  return '$' + rounded.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function CompGrid({ title, summaryItems, grid, subject, includeTimeAdj, highlightField, highlightLabel, roundIndicatedTo100 }) {
  if (!grid || grid.length === 0) return null;

  const numComps = grid.length;

  // Compute subject indicated value as mean of indicated_values
  const indVals = grid.map(g => g.indicated_value).filter(v => typeof v === 'number');
  const subjectIndicated = indVals.length > 0
    ? Math.round(indVals.reduce((a, b) => a + b, 0) / indVals.length)
    : null;

  const fmtIndicated = (n) => {
    if (!n && n !== 0) return '-';
    if (roundIndicatedTo100) return fmtRounded(n);
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const subjectAge = subject.construction_year
    ? CURRENT_YEAR - Math.round(subject.construction_year)
    : '-';

  const subjectTotalSf = subject.total_sf
    ? fmtNum(subject.total_sf)
    : '-';

  const subjectUpperAttic = (parseFloat(subject.upper_level_attic_sf) || 0);

  const adjKeys = includeTimeAdj
    ? ['TIME', 'AGE', 'MAIN_LEVEL', 'UPPER_LEVEL', 'BASEMENT', 'GARAGE', 'QUALITY']
    : ['AGE', 'MAIN_LEVEL', 'UPPER_LEVEL', 'BASEMENT', 'GARAGE', 'QUALITY'];

  const adjLabels = {
    TIME: 'TIME ADJUSTMENT ($)',
    AGE: 'AGE ADJUSTMENT ($)',
    MAIN_LEVEL: 'MAIN LEVEL ADJUSTMENT ($)',
    UPPER_LEVEL: 'UPPER LEVEL(S) ADJUSTMENT ($)',
    BASEMENT: 'BASEMENT ADJUSTMENT ($)',
    GARAGE: 'GARAGE ADJUSTMENT ($)',
    QUALITY: 'QUALITY/DESIGN ADJUSTMENT ($)',
  };

  function fmtAdjWithSign(n) {
    if (n === undefined || n === null) return '-';
    if (typeof n !== 'number') return n;
    if (n === 0) return '$0.00';
    const abs = '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n > 0 ? `+${abs}` : `-${abs}`;
  }

  function fmtNetGross(n, pct) {
    if (n === undefined || n === null) return '-';
    const abs = '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const sign = n >= 0 ? '+' : '-';
    const pctStr = typeof pct === 'number' ? ` (${pct.toFixed(1)}%)` : '';
    return `${sign}${abs}${pctStr}`;
  }

  function fmtGross(n, pct) {
    if (n === undefined || n === null) return '-';
    const abs = '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const pctStr = typeof pct === 'number' ? ` (${pct.toFixed(1)}%)` : '';
    return `${abs}${pctStr}`;
  }

  return (
    <div className="appeal-grid-section">
      <div className="appeal-grid-header">
        <h2 className="appeal-grid-title">{title}</h2>
        <div className="appeal-grid-summary">
          {summaryItems.map((item, i) => (
            <span key={i}>{item.label}: <strong>{item.value}</strong></span>
          ))}
        </div>
      </div>
      <div className="appeal-table-wrap">
        <table className="appeal-comp-table">
          <thead>
            <tr>
              <th className="appeal-sticky-col">PROPERTY DETAILS</th>
              <th className="appeal-subject-col">SUBJECT</th>
              {grid.map((_, i) => <th key={i}>COMP {i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {/* ADDRESS */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">ADDRESS</td>
              <td>{subject.address_combined || '-'}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.address_combined || '-'}</td>)}
            </tr>
            {/* PARCEL NUMBER */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">PARCEL NUMBER</td>
              <td>{subject.state_parcel_nbr || '-'}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.state_parcel_nbr || '-'}</td>)}
            </tr>
            {/* SALE DATE — sales only */}
            {includeTimeAdj && (
              <tr>
                <td className="appeal-sticky-col appeal-row-header">SALE DATE</td>
                <td>{subject.sale_date_1 || '-'}</td>
                {grid.map((g, i) => <td key={i}>{g.comp.sale_date_1 || '-'}</td>)}
              </tr>
            )}
            {/* SALE PRICE / ASSESSED VALUE — highlight */}
            <tr className="appeal-highlight-row">
              <td className="appeal-sticky-col appeal-row-header">{highlightLabel}</td>
              <td>{fmt$(subject[highlightField])}</td>
              {grid.map((g, i) => <td key={i}>{fmt$(g.comp[highlightField])}</td>)}
            </tr>
            {/* CONSTRUCTION YEAR */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">CONSTRUCTION YEAR</td>
              <td>{subject.construction_year ? Math.round(subject.construction_year) : '-'}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.construction_year ? Math.round(g.comp.construction_year) : '-'}</td>)}
            </tr>
            {/* AGE */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">AGE (years)</td>
              <td>{subjectAge}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.construction_year ? CURRENT_YEAR - Math.round(g.comp.construction_year) : '-'}</td>)}
            </tr>
            {/* TOTAL SF */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">TOTAL (sq ft)</td>
              <td>{subjectTotalSf}</td>
              {grid.map((g, i) => <td key={i}>{fmtNum(g.comp.total_sf)}</td>)}
            </tr>
            {/* MAIN LEVEL */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">MAIN LEVEL (sq ft)</td>
              <td>{fmtNum(subject.main_level_finished_sqft)}</td>
              {grid.map((g, i) => <td key={i}>{fmtNum(g.comp.main_level_finished_sqft)}</td>)}
            </tr>
            {/* UPPER + ATTIC */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">UPPER LEVEL + ATTIC (sq ft)</td>
              <td>{subjectUpperAttic > 0 ? fmtNum(subjectUpperAttic) : '-'}</td>
              {grid.map((g, i) => {
                const v = parseFloat(g.comp.upper_level_attic_sf) || 0;
                return <td key={i}>{v > 0 ? fmtNum(v) : '-'}</td>;
              })}
            </tr>
            {/* GARAGE */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">GARAGE (sq ft)</td>
              <td>{fmtNum(subject.garage_sf)}</td>
              {grid.map((g, i) => <td key={i}>{fmtNum(g.comp.garage_sf)}</td>)}
            </tr>
            {/* PARCEL ACREAGE */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">PARCEL ACREAGE (acres)</td>
              <td>{subject.parcel_acreage ? Number(subject.parcel_acreage).toFixed(2) : '-'}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.parcel_acreage ? Number(g.comp.parcel_acreage).toFixed(2) : '-'}</td>)}
            </tr>
            {/* GRADE */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">GRADE</td>
              <td>{subject.grade_code || '-'}</td>
              {grid.map((g, i) => <td key={i}>{g.comp.grade_code || '-'}</td>)}
            </tr>
            {/* DISTANCE */}
            <tr>
              <td className="appeal-sticky-col appeal-row-header">DISTANCE (km)</td>
              <td>0.00</td>
              {grid.map((g, i) => <td key={i}>{typeof g.comp.distance_km === 'number' ? g.comp.distance_km.toFixed(2) : '-'}</td>)}
            </tr>

            {/* Section header divider */}
            <tr className="appeal-section-header">
              <td colSpan={numComps + 2}>
                SUBJECT-SPECIFIC REGRESSION ADJUSTMENTS (FILTERED COEFFICIENTS)
              </td>
            </tr>

            {/* Adjustment rows */}
            {adjKeys.map(key => (
              <tr key={key}>
                <td className="appeal-sticky-col appeal-row-header">{adjLabels[key]}</td>
                <td>-</td>
                {grid.map((g, i) => {
                  const v = g.adjustments?.[key];
                  const isPos = typeof v === 'number' && v > 0;
                  const isNeg = typeof v === 'number' && v < 0;
                  return (
                    <td key={i} className={isPos ? 'appeal-adj-pos' : isNeg ? 'appeal-adj-neg' : ''}>
                      {fmtAdjWithSign(v)}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* NET ADJUSTMENT */}
            <tr className="appeal-total-row">
              <td className="appeal-sticky-col appeal-row-header">NET ADJUSTMENT ($)</td>
              <td>-</td>
              {grid.map((g, i) => {
                const n = g.net_adjustment;
                const pct = g.net_percent;
                const isPos = typeof n === 'number' && n > 0;
                const isNeg = typeof n === 'number' && n < 0;
                return (
                  <td key={i} className={isPos ? 'appeal-adj-pos' : isNeg ? 'appeal-adj-neg' : ''}>
                    {fmtNetGross(n, pct)}
                  </td>
                );
              })}
            </tr>

            {/* GROSS ADJUSTMENT */}
            <tr className="appeal-total-row">
              <td className="appeal-sticky-col appeal-row-header">GROSS ADJUSTMENT ($)</td>
              <td>-</td>
              {grid.map((g, i) => (
                <td key={i}>{fmtGross(g.gross_adjustment, g.gross_percent)}</td>
              ))}
            </tr>

            {/* INDICATED VALUE */}
            <tr className="appeal-indicated-row">
              <td className="appeal-sticky-col appeal-row-header">INDICATED VALUE ($)</td>
              <td>{fmtIndicated(subjectIndicated)}</td>
              {grid.map((g, i) => <td key={i}>{fmtIndicated(g.indicated_value)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AppealFlow() {
  const countyRaw = JSON.parse(localStorage.getItem('selectedCounty') || '{}');
  const countyName = countyRaw.county || '';
  const dbCounty = countyName.replace(/ County$/i, '');

  const [step, setStep] = useState(1);
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!dbCounty) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/appeal/autocomplete?q=${encodeURIComponent(address)}&county=${encodeURIComponent(dbCounty)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        setSuggestions(data.results || []);
        setShowSuggestions(true);
      } catch {}
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [address, dbCounty]);

  const handleAnalyze = async () => {
    if (!address.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/appeal/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address, county: dbCounty }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Analysis failed');
      setResult(data);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setAddress('');
    setSuggestions([]);
    setResult(null);
    setError('');
  };

  // ── Step 1: Address search ──────────────────────────────────────────────
  if (step === 1) return (
    <div className="appeal-flow">
      <div className="appeal-header">
        <h1 className="appeal-title">Property Assessment Appeal</h1>
        <p className="appeal-subtitle">
          Enter your address to see how your assessed value compares to recent sales.
          {countyName && <span className="appeal-county-badge">{countyName}</span>}
        </p>
      </div>

      <div className="appeal-step-card">
        <div className="appeal-address-wrap" ref={inputRef}>
          <label className="appeal-label">Property address</label>
          <input
            className="appeal-address-input"
            type="text"
            placeholder="Start typing your address…"
            value={address}
            onChange={e => { setAddress(e.target.value); setShowSuggestions(true); }}
            onFocus={() => address && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="appeal-suggestions">
              {suggestions.map((s, i) => (
                <li
                  key={i}
                  className="appeal-suggestion-item"
                  onMouseDown={() => {
                    setAddress(s.address_combined);
                    setShowSuggestions(false);
                  }}
                >
                  {s.address_combined}
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <div className="appeal-error">{error}</div>}

        <button
          className="appeal-btn-primary"
          onClick={handleAnalyze}
          disabled={!address.trim() || loading}
        >
          {loading ? 'Analyzing…' : 'Analyze property'}
        </button>
      </div>
    </div>
  );

  // ── Step 2: Comp grids ─────────────────────────────────────────────────
  if (step === 2 && result) {
    const {
      subject,
      salesCompGrid,
      assessedCompGrid,
      medianPricePerSf,
      medianIndicatedValue,
      medianPricePerSfAssessed,
      subjectIndicatedAssessed,
    } = result;

    const salesIndVals = (salesCompGrid || []).map(g => g.indicated_value).filter(v => typeof v === 'number');
    const salesSubjectIndicated = salesIndVals.length > 0
      ? Math.round(salesIndVals.reduce((a, b) => a + b, 0) / salesIndVals.length)
      : medianIndicatedValue;

    const assessedIndVals = (assessedCompGrid || []).map(g => g.indicated_value).filter(v => typeof v === 'number');
    const assessedSubjectIndicated = assessedIndVals.length > 0
      ? Math.round(assessedIndVals.reduce((a, b) => a + b, 0) / assessedIndVals.length)
      : subjectIndicatedAssessed;

    return (
      <div className="appeal-flow">
        <div className="appeal-header">
          <button className="appeal-back-btn" onClick={reset}>← New search</button>
          <h1 className="appeal-title">Comparable Properties</h1>
          <p className="appeal-subtitle">{subject.address_combined}</p>
        </div>

        <CompGrid
          title="Sales Comparables"
          subject={subject}
          summaryItems={[
            { label: 'Subject Sale Status', value: subject.sale_price_1 ? fmt$(subject.sale_price_1) : 'No Sale on Record' },
            { label: 'Median $/sq ft', value: fmt$(medianPricePerSf) },
            { label: 'Subject Indicated Value', value: fmt$(salesSubjectIndicated) },
            { label: 'Comparables Used', value: (salesCompGrid || []).length },
            { label: 'Data Source', value: 'Sales Data' },
            { label: 'Regression Method', value: 'Subject-Specific Model' },
          ]}
          grid={salesCompGrid || []}
          includeTimeAdj={true}
          highlightField="sale_price_1"
          highlightLabel="SALE PRICE ($)"
        />

        <CompGrid
          title="Assessed Value Comparables"
          subject={subject}
          summaryItems={[
            { label: 'Subject Assessed Value', value: fmt$(subject.most_recent_total_av) },
            { label: 'Median $/sq ft', value: fmt$(medianPricePerSfAssessed) },
            { label: 'Subject Indicated Value', value: fmt$(assessedSubjectIndicated) },
            { label: 'Comparables Used', value: (assessedCompGrid || []).length },
            { label: 'Data Source', value: 'Assessed Data' },
            { label: 'Regression Method', value: 'Subject-Specific Model' },
          ]}
          grid={assessedCompGrid || []}
          includeTimeAdj={false}
          highlightField="most_recent_total_av"
          highlightLabel="ASSESSED VALUE ($)"
          roundIndicatedTo100={true}
        />

        <div className="appeal-step2-actions">
          <button className="appeal-btn-primary" onClick={() => setStep(3)}>
            See appeal recommendation →
          </button>
        </div>
      </div>
    );
  }

  // ── Step 3: Appeal recommendation ─────────────────────────────────────
  if (step === 3 && result) {
    const { subject, appealRecommended, currentAssessment, subjectIndicatedAssessed, assessmentDifference } = result;
    const diffAbs = Math.abs(assessmentDifference);
    const diffPct = subjectIndicatedAssessed > 0 ? (diffAbs / subjectIndicatedAssessed) * 100 : 0;

    return (
      <div className="appeal-flow">
        <div className="appeal-header">
          <button className="appeal-back-btn" onClick={() => setStep(2)}>← Back to comps</button>
          <h1 className="appeal-title">Appeal Recommendation</h1>
          <p className="appeal-subtitle">{subject.address_combined}</p>
        </div>

        <div className={`appeal-recommendation-banner ${appealRecommended ? 'appeal-banner-red' : 'appeal-banner-green'}`}>
          <div className="appeal-banner-icon">{appealRecommended ? '⚠' : '✓'}</div>
          <div className="appeal-banner-text">
            <div className="appeal-banner-title">
              {appealRecommended ? 'Appeal Recommended' : 'Appeal Not Recommended'}
            </div>
            <div className="appeal-banner-desc">
              {appealRecommended
                ? `Your property appears over-assessed by ${fmt$(diffAbs)} (${diffPct.toFixed(1)}%)`
                : `Your assessment appears to be in line with market value`}
            </div>
          </div>
        </div>

        <div className="appeal-result-grid">
          <div className="appeal-result-card">
            <div className="appeal-result-label">Current Assessment</div>
            <div className="appeal-result-value">{fmt$(currentAssessment)}</div>
          </div>
          <div className="appeal-result-card">
            <div className="appeal-result-label">Indicated Assessed Value</div>
            <div className="appeal-result-value">{fmt$(subjectIndicatedAssessed)}</div>
          </div>
          <div className={`appeal-result-card ${appealRecommended ? 'appeal-result-over' : 'appeal-result-under'}`}>
            <div className="appeal-result-label">{appealRecommended ? 'Over-assessed by' : 'Under-assessed by'}</div>
            <div className="appeal-result-value">{fmt$(diffAbs)} <span className="appeal-result-pct">({diffPct.toFixed(1)}%)</span></div>
          </div>
        </div>

        {appealRecommended && (
          <div className="appeal-note">
            To file an appeal, submit <strong>Indiana Form 130</strong> with your county assessor's office.
            The filing deadline is typically <strong>June 15</strong> of the assessment year.
          </div>
        )}

        <button className="appeal-btn-secondary" onClick={reset}>Start new search</button>
      </div>
    );
  }

  return null;
}
